/**
 * GRANSKAREN FÖR RÖSTEN — VOICE_TRANSCRIBE:s svar in i kontraktets typer.
 *
 * SVARET ÄR ETT UTKAST OCH INGEN RAD: inga synkkolumner, ingen version, ingen
 * seq — bara det tolkningen tror att hantverkaren sa, i kontraktets enheter.
 * Därför står granskaren här och inte i rader.ts.
 *
 * RETURTYPEN ÄR BEVISET (se granska.ts): `Granskare<VoiceTranscribeResponse>`,
 * så ett fält som läggs till i VoiceDraft blir ett kompileringsfel här.
 *
 * ID:N GRANSKAS PÅ FORM, INTE MOT LAGRET. Servern har redan verifierat att
 * varje id pekar på en rad anroparen får se (worker/src/modules/voice/
 * interpret.ts). Skärmen slår ändå upp dem i ögonblicksbilden innan den fyller
 * i — ett id på en order som hann arkiveras mellan pull och röst ska bli
 * "välj själv", inte en tom rad.
 */

import type { VoiceDraft, VoiceDraftField, VoiceTranscribeResponse } from '@delat/types';
import {
  arbetsdag,
  enAv,
  lista,
  meter,
  minuter,
  nullbar,
  objekt,
  taskId,
  text,
  workOrderId,
} from '../granska';
import type { Granskare } from '../granska';
import { FORDONEN } from './rader';

/**
 * De fält utkastet kan vara osäkert på. Avskrivna ur VoiceDraftField av samma
 * skäl som listorna i rader.ts: kontraktet ger unionen som TYP, inte som DATA.
 * Raden `UttommandeUtkastfalt` faller i tsc om ett värde läggs till där borta.
 */
export const UTKASTFALTEN = [
  'minutes',
  'task_ids',
  'work_order_id',
  'work_date',
  'meters',
  'vehicle',
] as const satisfies readonly VoiceDraftField[];

type Tacker<TUnion extends string, TLista extends readonly string[]> = [TUnion] extends [
  TLista[number],
]
  ? true
  : never;

export type UttommandeUtkastfalt = Tacker<VoiceDraftField, typeof UTKASTFALTEN>;

const granskaUtkast: Granskare<VoiceDraft> = (v) => {
  const o = objekt(v, 'draft');
  const unmatched = objekt(o['unmatched'], 'draft.unmatched');
  return {
    minutes: nullbar(o['minutes'], 'draft.minutes', (x) => minuter(x, 'minutes')),
    task_ids: lista(o['task_ids'], 'draft.task_ids', (x) => taskId(x, 'task_id')),
    work_order_id: nullbar(o['work_order_id'], 'draft.work_order_id', (x) =>
      workOrderId(x, 'work_order_id'),
    ),
    work_date: nullbar(o['work_date'], 'draft.work_date', (x) => arbetsdag(x, 'work_date')),
    meters: nullbar(o['meters'], 'draft.meters', (x) => meter(x, 'meters')),
    vehicle: nullbar(o['vehicle'], 'draft.vehicle', (x) => enAv(x, 'vehicle', FORDONEN)),
    note: nullbar(o['note'], 'draft.note', (x) => text(x, 'note')),
    unsure: lista(o['unsure'], 'draft.unsure', (x) => enAv(x, 'unsure', UTKASTFALTEN)),
    unmatched: {
      work_order: nullbar(unmatched['work_order'], 'draft.unmatched.work_order', (x) =>
        text(x, 'work_order'),
      ),
      tasks: lista(unmatched['tasks'], 'draft.unmatched.tasks', (x) => text(x, 'task')),
    },
  };
};

export const granskaRostsvar: Granskare<VoiceTranscribeResponse> = (v) => {
  const o = objekt(v, 'voice');
  return {
    text: text(o['text'], 'voice.text'),
    draft: granskaUtkast(o['draft']),
  };
};
